/**
 * The lifecycle policy screen's own state machine (contract §11).
 *
 * `policy` is the server's last-known-good document; `draft` is `policy` plus the
 * operator's in-progress edits; `patch` is the MINIMAL deep diff of the two, in the exact
 * shape §6 PUT /policy expects ("Objects merge; lists are replaced whole"). `patch` is
 * computed here rather than sent as the whole `draft`, because a full-document PUT would
 * silently re-assert every OTHER operator's concurrent edit as this operator's own.
 *
 * `api` is injectable (default `lifecycleApi`) so a test can pass a jest.fn() double
 * without touching the real axios instance — the same DI seam `gatewayApi` uses
 * throughout `gateway/components/`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import lifecycleApi from "./api";
import { apiErrorCode } from "../../lib/apiErrors";

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/** Deep clone via JSON: every field in this policy document is JSON-safe (§2). */
const clone = (v) => (v === undefined ? v : JSON.parse(JSON.stringify(v)));

const sameArray = (a, b) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * The minimal deep diff of `next` against `base`: only the leaves and lists that differ,
 * nested under the same keys. Lists are replaced whole (never merged element-by-element) —
 * a diff that emitted only the changed array INDEX would violate §6's "lists are replaced
 * whole" and let the server see a partial list.
 */
function diffPatch(base, next) {
  if (Array.isArray(next)) return sameArray(base, next) ? undefined : next;
  if (isPlainObject(next)) {
    const out = {};
    let changed = false;
    for (const key of Object.keys(next)) {
      const sub = diffPatch(isPlainObject(base) ? base[key] : undefined, next[key]);
      if (sub !== undefined) { out[key] = sub; changed = true; }
    }
    return changed ? out : undefined;
  }
  return Object.is(base, next) ? undefined : next;
}

/** Immutable set of a dotted path ("trial.reminders.lead_days") inside a plain object tree. */
function setPath(obj, path, value) {
  const parts = path.split(".");
  const next = clone(obj);
  let node = next;
  for (let i = 0; i < parts.length - 1; i += 1) {
    node = node[parts[i]];
  }
  node[parts[parts.length - 1]] = value;
  return next;
}

/** §6's `violations: [{field, rule}]` as `{field: [rule, ...]}` — a field can carry >1 rule. */
function violationsMap(list) {
  const map = {};
  if (!Array.isArray(list)) return map;
  for (const v of list) {
    if (!v || typeof v.field !== "string" || typeof v.rule !== "string") continue;
    (map[v.field] ||= []).push(v.rule);
  }
  return map;
}

export default function useLifecyclePolicy({ api = lifecycleApi } = {}) {
  const [status, setStatus] = useState("loading");
  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [stripeControlled, setStripeControlled] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [violations, setViolations] = useState({});
  const [saving, setSaving] = useState(false);

  const applyGetBody = useCallback((body) => {
    setPolicy(body.policy);
    setDraft(clone(body.policy));
    setDefaults(body.defaults);
    setBounds(body.bounds);
    setStripeControlled(Array.isArray(body.stripe_controlled) ? body.stripe_controlled : []);
    setUpdatedAt(body.updated_at ?? null);
    setUpdatedBy(body.updated_by ?? null);
  }, []);

  const reload = useCallback(async () => {
    setStatus("loading");
    try {
      const body = await api.getPolicy();
      applyGetBody(body);
      setViolations({});
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [api, applyGetBody]);

  useEffect(() => { reload(); }, [reload]);

  const patch = useMemo(() => {
    if (!policy || !draft) return {};
    return diffPatch(policy, draft) ?? {};
  }, [policy, draft]);

  const dirty = Object.keys(patch).length > 0;

  const setField = useCallback((path, value) => {
    setDraft((prev) => (prev ? setPath(prev, path, value) : prev));
    // A field the operator just touched is no longer explained by a PREVIOUS violation —
    // leaving the stale message up would blame the current value for the old one's fault.
    setViolations((prev) => {
      if (!(path in prev)) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setDraft(policy ? clone(policy) : policy);
    setViolations({});
  }, [policy]);

  const save = useCallback(async (stepUpToken) => {
    setSaving(true);
    try {
      const body = await api.putPolicy(patch, stepUpToken);
      applyGetBody(body);
      setViolations({});
      return { ok: true };
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === "POLICY_INVALID") {
        const detail = e?.response?.data?.detail;
        setViolations(violationsMap(detail?.violations));
        return { ok: false, code };
      }
      return { ok: false, code: code || null };
    } finally {
      setSaving(false);
    }
  }, [api, patch, applyGetBody]);

  return {
    status,
    policy, defaults, bounds, stripeControlled, updatedAt, updatedBy,
    draft,
    dirty,
    patch,
    setField,
    reset,
    save,
    violations,
    saving,
    reload,
  };
}
